import type { FunctionComponent } from "preact";
import Separator from "./components/Separator.tsx";
import Button from "./components/Button.tsx";
import { useEffect, useRef, useState } from "preact/hooks";
import { useQuery, useQueryClient } from "@tanstack/preact-query";
import useApi, {
    type Collection,
    type Test,
    type TestPreview,
    type TestQuestion,
    type TestResult,
} from "./tests-api.ts";
import Spinner from "./components/Spinner.tsx";
import NiceModal, { type NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import Modal from "./components/Modal.tsx";
import * as stylex from "@stylexjs/stylex";
import { useImmer } from "use-immer";
import Form, { type CustomFileProvider } from "./components/Form.tsx";
import ActionSheet from "./components/ActionSheet.tsx";
import deleteSprite from "./assets/edit-delete.png";
import { customAlphabet } from "nanoid";
import asObjectURL from "./utils/asObjectURL.ts";
import { Flip } from "gsap/Flip";
import { useGSAP } from "@gsap/react";
import OffsetFetch from "./components/OffsetFetch.tsx";
import placeholderPic from "./assets/image-placeholder.png";
import type { ChangeEvent } from "preact/compat";
import TestApplet from "./TestApplet.tsx";

const styles = stylex.create({
    editorControls: {
        gap: 10,
        display: "flex",
        marginBottom: 20,
    },
    editorControlsBottom: {},
    editorControlsSticky: {
        position: "sticky",
        top: 0,
        zIndex: 102,
        backgroundColor: "#FFF",
        padding: "0 10px",
        paddingBottom: 10,
        marginBottom: 5,
    },
    coverImage: {
        objectFit: "contain",
        aspectRatio: "3/2",
        boxShadow: "0 0 2px 0 color-mix(in prophoto-rgb, #000 40%, transparent)",
        borderRadius: "4px",
        backgroundColor: "#dae4ed",
        width: "100%",
    },
    questionList: {
        gap: 15,
        display: "flex",
        flexDirection: "column",
        marginBottom: 20,
    },
    questionEntry: {
        gap: 10,
        display: "grid",
        gridTemplateColumns: "175px 1fr",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#DDD",
        borderRadius: 6,
        padding: 10,
    },
    questionTitle: {
        color: "#666",
        paddingLeft: 10,
        paddingBottom: 5,
        marginBottom: 10,
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        borderBottomColor: "#CCC",
        fontFamily: "'Inter Variable', sans-serif",
        fontSize: 15,
    },
    questionOptions: {
        color: "#666",
        margin: 0,
        fontSize: 13,
        fontFamily: "'Inter Variable', sans-serif",
        paddingLeft: 20,
    },
    optionList: {
        gap: 10,
        display: "flex",
        flexDirection: "column",
    },
    optionEdit: {
        gap: 10,
        display: "grid",
        gridTemplateColumns: "1fr 20px",
        alignItems: "center",
    },
    resultEntry: {
        gridTemplateColumns: "250px 1fr",
    },
    resultCaption: {
        color: "#666",
        fontSize: 14,
        fontFamily: "'Inter Variable', sans-serif",
    },
    resultOptionsList: {
        gap: 22,
        display: "flex",
        flexDirection: "column",
    },
    testGallery: {
        gap: 10,
        display: "flex",
        flexDirection: "column",
    },
    testGalleryEntry: {
        gap: 20,
        padding: 15,
        display: "grid",
        backgroundColor: "#FAFAFA",
        gridTemplateColumns: "70px 1fr",
    },
    testGalleryEntryImage: {
        objectFit: "contain",
        aspectRatio: "1",
        boxShadow: "0 0 2px 0 color-mix(in prophoto-rgb, #000 40%, transparent)",
        borderRadius: "4px",
        backgroundColor: "#dae4ed",
        backgroundSize: "contain",
        color: "transparent" /* hide alts during loading */,
        width: "100%",
    },
    testGalleryEntryTitle: {
        color: "#555",
        fontSize: 15,
        fontFamily: "'Inter Variable', sans-serif",
        marginBottom: 5,
    },
    testGalleryEntryTitleWarning: {
        fontSize: 12,
        marginLeft: 5,
        verticalAlign: "center",
    },
    testGalleryEntryCaption: {
        color: "#737373",
        fontSize: 13,
        fontFamily: "'Inter Variable', sans-serif",
    },
    imageSearchContainer: {
        gap: 10,
        height: 420,
        display: "grid",
        gridTemplateRows: "min-content 1fr",
    },
    imageSearchResults: {
        gap: 20,
        display: "flex",
        flexWrap: "wrap",
        overflowY: "auto",
        justifyContent: "space-evenly",
    },
    imageSearchResult: {
        cursor: "pointer",
        borderRadius: 5,
        padding: 12,
        backgroundColor: {
            default: "transparent",
            ":hover": "#EEE",
        },
    },
    imageSearchResultImg: {
        height: 128,
        maxWidth: 128,
    },
});

interface BalleryFragmentProps {
    edit: (test: string | null) => void;
    drafts: boolean;
}

const GalleryFragment: FunctionComponent<BalleryFragmentProps> = ({ edit, drafts }) => {
    const client = useQueryClient();

    const deleteTest = (id: string, offset: string) => {
        Modal.useLoader("Удаление теста", async () => {
            await (await useApi()).removeTest(id, drafts);
            await client.invalidateQueries({
                queryKey: ["tests", drafts ? "drafts" : "my", "offset", offset],
            });
        });
    };

    const syncTest = async (id: string) => {
        const api = await await useApi();
        const sync = drafts ? api.syncTest : api.syncTestReverse;
        const error = await Modal.useLoader("Синхронизация теста (до 60 сек)", async () => {
            await sync(id);
            if (!drafts) {
                await client.invalidateQueries({
                    queryKey: ["tests"],
                });
            }
        });

        if (!error) await NiceModal.show(Modal.Alert, { title: "Тест успешно синхронизирован" });
    };

    return (
        <OffsetFetch
            queryKey={["tests", drafts ? "drafts" : "my"]}
            queryFn={async (offset: string): Promise<Collection<TestPreview>> =>
                await (await useApi()).getTests(drafts ? "drafts" : "my", offset)
            }
            item={(test, offset) => (
                <ActionSheet
                    actions={[
                        ...(drafts
                            ? [
                                  {
                                      label: "Редактировать",
                                      onClick: () => edit(test.id),
                                  },
                                  {
                                      label: "Создать (обновить) публичную копию",
                                      onClick: () => syncTest(test.id),
                                  },
                              ]
                            : test.dirty
                              ? [
                                    {
                                        label: "Обновить из черновика",
                                        onClick: () => syncTest(test.id),
                                    },
                                ]
                              : []),
                        {
                            label: "Удалить",
                            kind: "danger",
                            onClick: () => deleteTest(test.id, offset),
                        },
                    ]}
                >
                    <div {...stylex.props(styles.testGalleryEntry)}>
                        <img
                            {...stylex.props(styles.testGalleryEntryImage)}
                            key={test.icon}
                            alt={test.title}
                            draggable={false}
                            src={test.icon as string}
                            style={{ backgroundImage: `url(${placeholderPic})` }}
                        />
                        <div>
                            <div {...stylex.props(styles.testGalleryEntryTitle)}>
                                {test.title}
                                {!drafts && test.dirty ? (
                                    <em {...stylex.props(styles.testGalleryEntryTitleWarning)}>
                                        (требует обновления)
                                    </em>
                                ) : null}
                            </div>
                            <div {...stylex.props(styles.testGalleryEntryCaption)}>
                                {test.description}
                            </div>
                        </div>
                    </div>
                </ActionSheet>
            )}
            Wrapper={({ children }) => <div {...stylex.props(styles.testGallery)}>{children}</div>}
        />
    );
};

const Gallery: FunctionComponent<{ builder: (test: string | null) => void }> = ({ builder }) => {
    return (
        <>
            <Separator label="Мои тесты-черновики (скрыты от всех)">
                <div {...stylex.props(styles.editorControls)}>
                    <Button label="Добавить тест" onClick={() => builder("")} />
                </div>

                <GalleryFragment drafts={true} edit={builder} />
            </Separator>

            <Separator label="Мои публичные тесты (можно показывать другим пользователям)">
                <GalleryFragment drafts={false} edit={builder} />
            </Separator>
        </>
    );
};

const ImageSearchProvider: FunctionComponent<NiceModalHocProps> = NiceModal.create(() => {
    const modal = useModal();

    const [searchQuery, setsearchQuery] = useState<string>("");
    let searchTimeout = useRef<number | undefined>(undefined);
    const searchFieldUpdated = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        if (value.length < 3) return;

        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => setsearchQuery(value), 1000) as unknown as number;
    };

    const resolveWithImage = async ({ url: src }: { url: string }) => {
        let image: ImageBitmap | null = null;
        let err = await Modal.useLoader("Загрузка изображения", async () => {
            const resp = await fetch(src);
            image = await createImageBitmap(await resp.blob());
        });

        if (err || null == image) return;

        const canvas = new OffscreenCanvas(
            (image as ImageBitmap).width,
            (image as ImageBitmap).height,
        );
        canvas!.getContext("2d")!.drawImage(image, 0, 0);

        await Modal.useLoader("Конвертация изображения", async () => {
            const blob = new File(
                [await canvas!.convertToBlob({ type: "image/webp" })],
                "image.webp",
            );
            modal.resolve(blob);
            modal.remove();
        });
    };

    return (
        <Modal.Abstract style={{}}>
            <div {...stylex.props(styles.imageSearchContainer)}>
                <div style={{ padding: 2 }}>
                    <Form.Input
                        placeholder="Поиск картинок в TikTok (от 3 символов)"
                        style={{ fontSize: 16 }}
                        onChange={(e) => searchFieldUpdated(e)}
                    />
                </div>
                <OffsetFetch
                    queryKey={["search", searchQuery]}
                    queryFn={async (offset) =>
                        await (
                            await useApi()
                        ).searchImages(searchQuery.length == 0 ? "biden" : searchQuery, offset)
                    }
                    Wrapper={({ children }) => (
                        <div {...stylex.props(styles.imageSearchResults)}>{children}</div>
                    )}
                    item={(image) => (
                        <div
                            {...stylex.props(styles.imageSearchResult)}
                            onClick={() => resolveWithImage(image)}
                        >
                            <img
                                {...stylex.props(styles.imageSearchResultImg)}
                                alt={image.alt}
                                src={image.url}
                                draggable={false}
                            />
                        </div>
                    )}
                />
            </div>
        </Modal.Abstract>
    );
});

const TEST_TEMPLATE: Test = {
    id: "",
    title: "",
    icon: null,
    cover: null,
    description: "",
    price: 3,
    author: null,
    draft: true,
    questions: [],
    results: [],
};

const idProvider = customAlphabet("6789BCDFGHJKLMNPQRTWbcdfghjkmnpqrtwz");
const nextId = () => idProvider(10);

const imageSearchProviders: (findWhat: string) => CustomFileProvider[] = (findWhat) => [
    {
        label: `Найти ${findWhat}`,
        async getFile() {
            return NiceModal.show(ImageSearchProvider);
        },
    },
];

const Q_MAX_OPTIONS = 10;
const QuestionEditor: FunctionComponent<NiceModalHocProps & { question_: TestQuestion }> =
    NiceModal.create(({ question_ }) => {
        const modal = useModal();
        const [question, setQuestion] = useImmer<TestQuestion>(question_);

        const addOption = () =>
            setQuestion((q) => {
                q.options[nextId()] = "Вариант ответа";
            });

        return (
            <Modal.Abstract
                closable={true}
                style={{ width: "95%" }}
                onClose={
                    modal &&
                    (() => {
                        modal.resolve(question);
                        modal.remove();

                        return false;
                    })
                }
            >
                <Form.Row label="Картинка">
                    <Form.ImageInput
                        label="Изменить картинку"
                        previewWidth={310}
                        aspectRatio={3 / 2}
                        initialImage={question.cover}
                        fileProviders={imageSearchProviders("картинку")}
                        actionButtonStyle={{ display: "block", marginBottom: 10 }}
                        onImageBlob={(blob) =>
                            setQuestion((q) => {
                                q.cover = blob;
                            })
                        }
                    />
                </Form.Row>
                <Form.Row label="Вопрос">
                    <Form.TextArea
                        onChange={(e) =>
                            setQuestion((q) => {
                                q.title = e.currentTarget.value;
                            })
                        }
                    >
                        {question.title}
                    </Form.TextArea>
                </Form.Row>
                <Form.Row label="Варианты ответов">
                    <div {...stylex.props(styles.editorControls)}>
                        <Button
                            label="Добавить вариант ответа"
                            onClick={addOption}
                            disabled={Object.values(question.options).length >= Q_MAX_OPTIONS}
                        />
                    </div>
                    <div {...stylex.props(styles.optionList)}>
                        {Object.entries(question.options).map(([optionId, option]) => (
                            <div {...stylex.props(styles.optionEdit)} key={optionId}>
                                <Form.Input
                                    value={option}
                                    onChange={(e) =>
                                        setQuestion((q) => {
                                            q.options[optionId] = e.currentTarget.value;
                                        })
                                    }
                                />

                                <img
                                    width="22"
                                    draggable={false}
                                    src={deleteSprite}
                                    alt="Удалить вариант ответа"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        setQuestion((q) => {
                                            delete q.options[optionId];
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Form.Row>
            </Modal.Abstract>
        );
    });

const ResultEditor: FunctionComponent<
    NiceModalHocProps & { questions: TestQuestion[]; result_: TestResult }
> = NiceModal.create(({ questions, result_ }) => {
    const modal = useModal();
    const [result, setResult] = useImmer<TestResult>(result_);

    return (
        <Modal.Abstract
            closable={true}
            style={{ width: "95%" }}
            onClose={
                modal &&
                (() => {
                    modal.resolve(result);
                    modal.remove();

                    return false;
                })
            }
        >
            <Form.Row label="Картинка">
                <Form.ImageInput
                    label="Изменить картинку"
                    previewWidth={310}
                    aspectRatio={3 / 2}
                    initialImage={result.cover}
                    fileProviders={imageSearchProviders("картинку")}
                    actionButtonStyle={{ display: "block", marginBottom: 10 }}
                    onImageBlob={(blob) =>
                        setResult((r) => {
                            r.cover = blob;
                        })
                    }
                />
            </Form.Row>
            <Form.Row label="Название">
                <Form.Input
                    value={result.title}
                    placeholder="Ты — Хомура Акеми"
                    onChange={(e) =>
                        setResult((r) => {
                            r.title = e.currentTarget.value;
                        })
                    }
                />
            </Form.Row>
            <Form.Row label="Текст">
                <Form.TextArea
                    placeholder="Ваше описание этого результата"
                    onChange={(e) =>
                        setResult((r) => {
                            r.caption = e.currentTarget.value;
                        })
                    }
                >
                    {result.caption}
                </Form.TextArea>
            </Form.Row>
            <Form.Row label="Выбор ответов">
                <div {...stylex.props(styles.resultOptionsList)}>
                    {questions.map((q) => (
                        <div key={q.id}>
                            <div {...stylex.props(styles.questionTitle)} style={{ fontSize: 13 }}>
                                {q.title}
                            </div>
                            <div style={{ marginBottom: 15 }}>
                                {Object.entries(q.options).map(([optionId, option]) => (
                                    <Form.Radio
                                        key={optionId}
                                        label={option}
                                        name={`Q${q.id}`}
                                        checked={optionId == result.options[q.id]}
                                        onChange={(e) => {
                                            if (e.currentTarget.checked)
                                                setResult((r) => {
                                                    r.options[q.id] = optionId;
                                                });
                                        }}
                                    />
                                ))}
                            </div>
                            <div>
                                <Form.Radio
                                    name={`Q${q.id}`}
                                    checked={null == result.options[q.id]}
                                    label="Ответ на этот вопрос не будет учитываться"
                                    onChange={(e) => {
                                        if (e.currentTarget.checked)
                                            setResult((r) => {
                                                delete r.options[q.id];
                                            });
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </Form.Row>
        </Modal.Abstract>
    );
});

const Builder: FunctionComponent<{ id_: string; exit: () => void }> = ({ id_, exit }) => {
    const [id, setId] = useState(id_);
    const {
        data: savedTest,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["test", id],
        queryFn: async () => (await useApi()).getTest(id, true, false),
        enabled: id.length > 0,
        initialData: id.length > 0 ? undefined : TEST_TEMPLATE,
        refetchOnWindowFocus: false,
    });

    const [dirty, setDirty] = useState<boolean | null>(null);
    const [test, setTest] = useImmer<Test | null>(null);
    useEffect(() => {
        setTest(savedTest || null);
        setDirty(false);

        return () => {};
    }, [savedTest]);

    const qListRef = useRef<HTMLDivElement | null>(null);
    const rListRef = useRef<HTMLDivElement | null>(null);
    const qListState = useRef(null);
    const rListState = useRef(null);

    const mutateQuestions = (cb: () => void) => {
        // @ts-ignore
        qListState.current = Flip.getState(qListRef.current!.children);
        cb();
    };

    const mutateResults = (cb: () => void) => {
        // @ts-ignore
        rListState.current = Flip.getState(rListRef.current!.children);
        cb();
    };

    useGSAP(() => {
        if (null != qListState.current) {
            Flip.from(qListState.current, {
                duration: 0.4,
                ease: "elastic.out(1.3,0.5)",
            });

            qListState.current = null;
        }

        if (null != rListState.current) {
            Flip.from(rListState.current, {
                duration: 0.4,
                ease: "elastic.out(1.3,0.5)",
            });

            rListState.current = null;
        }
    }, [test]);

    useEffect(() => {
        if (null != dirty && !dirty) setDirty(true);
    }, [test]);

    const client = useQueryClient();
    const pushTestToServer = async () => {
        if (null == test) return;

        const maybeError = await Modal.useLoader("Сохранение теста (до 30 сек)", async () => {
            const new_id = await (await useApi()).saveTest(structuredClone(test));
            if (new_id != id) setId(new_id);
            else await client.invalidateQueries({ queryKey: ["test", id] });
        });

        if (maybeError) console.error(maybeError);
        else setDirty(false);
    };

    const reorderQuestion = (id: string, up: boolean) => {
        mutateQuestions(() =>
            setTest((t) => {
                if (null == t) return;

                for (let i = 0; i < t.questions.length; i++) {
                    if (id == t.questions[i].id) {
                        if (up ? i < 1 : i == t.questions.length - 1) return;

                        const from = i,
                            to = up ? i - 1 : i + 1;
                        const tmp = t.questions[to];
                        t.questions[to] = t.questions[from];
                        t.questions[from] = tmp;
                        break;
                    }
                }
            }),
        );
    };

    const addQuestion = async () => {
        const newQ = (await NiceModal.show(QuestionEditor, {
            question_: {
                id: nextId(),
                title: "Новый вопрос",
                cover: null,
                options: {
                    [nextId()]: "Вариант 1",
                    [nextId()]: "Вариант 2",
                    [nextId()]: "Вариант 3",
                },
            },
        })) as TestQuestion | null;

        if (null == newQ) return;

        mutateQuestions(() =>
            setTest((test) => {
                test!.questions.push(newQ);
            }),
        );
    };

    const addResult = async () => {
        const newR = (await NiceModal.show(ResultEditor, {
            questions: test!.questions,
            result_: {
                id: nextId(),
                cover: null,
                title: "",
                caption: "",
                options: {},
            },
        })) as TestResult | null;

        if (null == newR) return;

        mutateResults(() =>
            setTest((test) => {
                test!.results.push(newR);
            }),
        );
    };

    const previewTest = async () => {
        await NiceModal.show(TestApplet, {
            testPreview: test,
            acquireFullTest: () => new Promise((r) => setTimeout(() => r(test), 400)),
        });
    };

    if (isLoading || null == test)
        return <Spinner.Loader label="Загрузка редактора" fullHeight={true} />;

    if (error) {
        NiceModal.show(Modal.Alert, { title: error }).then(exit);

        return;
    }

    return (
        <>
            <div {...stylex.props(styles.editorControls, styles.editorControlsSticky)}>
                <Button label="Назад к списку" onClick={exit} />
                <Button label="Запустить тест" onClick={previewTest} />
                <Button
                    kind={dirty ? "success" : "default"}
                    disabled={!dirty}
                    label="Сохранить тест"
                    onClick={pushTestToServer}
                />
            </div>

            <Separator label="Основные параметры теста">
                <Form.Row label="Название">
                    <Form.Input
                        value={test.title}
                        placeholder="Какой ты фрукт?"
                        onChange={(e) =>
                            setTest((t) => {
                                t!.title = e.currentTarget.value;
                            })
                        }
                    />
                </Form.Row>
                <Form.Row label="Описание">
                    <Form.TextArea
                        placeholder="Пройдя этот тест ты узнаешь, каким фруктом или ягодой я тебя считаю."
                        onChange={(e) =>
                            setTest((t) => {
                                t!.description = e.currentTarget.value;
                            })
                        }
                    >
                        {test.description}
                    </Form.TextArea>
                </Form.Row>
                <Form.Row label="Иконка">
                    <Form.ImageInput
                        label="Изменить иконку"
                        aspectRatio={1}
                        previewWidth={100}
                        initialImage={test.icon}
                        fileProviders={imageSearchProviders("иконку")}
                        onImageBlob={(blob) =>
                            setTest((t) => {
                                t!.icon = blob;
                            })
                        }
                    />
                </Form.Row>
                <Form.Row label="Обложка">
                    <Form.ImageInput
                        label="Изменить обложку"
                        aspectRatio={3 / 2}
                        previewWidth={200}
                        initialImage={test.cover}
                        fileProviders={imageSearchProviders("обложку")}
                        onImageBlob={(blob) =>
                            setTest((t) => {
                                t!.cover = blob;
                            })
                        }
                    />
                </Form.Row>
            </Separator>

            <Separator label="Список вопросов теста">
                <div {...stylex.props(styles.editorControls)}>
                    <Button label="Добавить вопрос" onClick={addQuestion} />
                </div>

                <div {...stylex.props(styles.questionList)} ref={qListRef}>
                    {test!.questions.map((question) => (
                        <ActionSheet
                            key={question.id}
                            actions={[
                                {
                                    label: "Редактировать",
                                    onClick: async () => {
                                        const newQ = (await NiceModal.show(QuestionEditor, {
                                            question_: question,
                                        })) as TestQuestion | null;

                                        if (null == newQ) return;

                                        // Not calling mutate cuz order doesn't change :P
                                        setTest((test) => {
                                            test!.questions = test!.questions.map((q) =>
                                                question.id == q.id ? newQ : q,
                                            );
                                        });
                                    },
                                },
                                {
                                    label: "▲",
                                    onClick: () => reorderQuestion(question.id, true),
                                },
                                {
                                    label: "▼",
                                    onClick: () => reorderQuestion(question.id, false),
                                },
                                {
                                    label: "Удалить",
                                    kind: "danger",
                                    onClick: () =>
                                        mutateQuestions(() =>
                                            setTest((test) => {
                                                test!.questions = test!.questions.filter(
                                                    (q) => q.id != question.id,
                                                );
                                            }),
                                        ),
                                },
                            ]}
                        >
                            <div {...stylex.props(styles.questionEntry)}>
                                <img
                                    {...stylex.props(styles.coverImage)}
                                    alt={question.title}
                                    draggable={false}
                                    src={asObjectURL(question.cover)}
                                />
                                <div>
                                    <div {...stylex.props(styles.questionTitle)}>
                                        {question.title}
                                    </div>
                                    <ul {...stylex.props(styles.questionOptions)}>
                                        {Object.entries(question.options).map(
                                            ([optionId, option]) => (
                                                <li key={optionId}>{option}</li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </ActionSheet>
                    ))}
                </div>

                {test!.questions.length > 1 && (
                    <div {...stylex.props(styles.editorControls, styles.editorControlsBottom)}>
                        <Button label="Добавить вопрос" onClick={addQuestion} />
                    </div>
                )}
            </Separator>

            <Separator label="Результаты теста">
                <div {...stylex.props(styles.editorControls)}>
                    <Button label="Добавить результат" onClick={addResult} />
                </div>

                <div {...stylex.props(styles.questionList)} ref={rListRef}>
                    {test!.results.map((result) => (
                        <ActionSheet
                            key={result.id}
                            actions={[
                                {
                                    label: "Редактировать",
                                    onClick: async () => {
                                        const newR = (await NiceModal.show(ResultEditor, {
                                            questions: test.questions,
                                            result_: result,
                                        })) as TestResult | null;

                                        if (null == newR) return;

                                        // Not calling mutate cuz order doesn't change :P
                                        setTest((test) => {
                                            test!.results = test!.results.map((r) =>
                                                r.id == result.id ? newR : r,
                                            );
                                        });
                                    },
                                },
                                {
                                    label: "Удалить",
                                    kind: "danger",
                                    onClick: () =>
                                        mutateResults(() =>
                                            setTest((test) => {
                                                test!.results = test!.results.filter(
                                                    (r) => r.id != result.id,
                                                );
                                            }),
                                        ),
                                },
                            ]}
                        >
                            <div {...stylex.props(styles.questionEntry, styles.resultEntry)}>
                                <img
                                    {...stylex.props(styles.coverImage)}
                                    alt={result.caption}
                                    draggable={false}
                                    src={asObjectURL(result.cover)}
                                />
                                <div {...stylex.props(styles.resultCaption)}>
                                    <strong>
                                        {/[?!,.]$/.test(result.title)
                                            ? result.title.substring(0, result.title.length - 1)
                                            : result.title}
                                        {result.title.length > 0 ? <>.&nbsp;</> : <></>}
                                    </strong>
                                    <span>{result.caption}</span>
                                </div>
                            </div>
                        </ActionSheet>
                    ))}
                </div>

                {test!.results.length > 0 && (
                    <div {...stylex.props(styles.editorControls, styles.editorControlsBottom)}>
                        <Button label="Добавить результат" onClick={addResult} />
                    </div>
                )}
            </Separator>
        </>
    );
};

const Editor: FunctionComponent<{}> = () => {
    const [test, setTest] = useState<string | null>(null);

    return null == test ? (
        <Gallery builder={setTest} />
    ) : (
        <Builder id_={test} exit={() => setTest(null)} />
    );
};

export default Editor;
